import { isLessThanOne } from "../utils/helpers";

const normalizeBarValue = (value) => value ?? 0;
const normalizeBarDisplayValue = (value) =>
    isLessThanOne(value) ? "<1" : (value ?? 0);
const getOrderedLabels = (order, fallback) => [
    ...new Set(Array.isArray(order) ? order : fallback),
];
const BENCHMARK_DONUT_SLOTS = [
    "selectedorganizations",
    "industrypeers",
    "unumblock",
];

// Donut charts render groups by slot (organization, industry peers, Unum
// block), so place each group by compareType when the API provides it.
const getBenchmarkDonutGroups = (groups) => {
    if (!groups.some((group) => group?.compareType)) return groups;
    const slots = BENCHMARK_DONUT_SLOTS.map((compareType) =>
        groups.find(
            (group) => String(group?.compareType).toLowerCase() === compareType
        )
    );
    let slotCount = slots.length;
    while (slotCount > 0 && !slots[slotCount - 1]) slotCount -= 1;
    return slots.slice(0, slotCount).map((group) => group || { values: [] });
};

const formatDashboardData = (dashboardData) => {
    const charts = dashboardData?.result?.charts;
    if (!Array.isArray(charts)) return [];

    const idMap = {
        0: "claimsByAge",
        1: "genderDistribution",
        2: "claimsByTenure",
        3: "Claimsbydiagnosis",
        4: "Averageclaimdurationindays",
        5: "Reasonforclosure",
        6: "ltdSsOffsets",
        7: "claimsByWCOffset",
    };

    return charts.map((chart) => {
        const {
            id,
            title,
            description,
            accessibilityText,
            variant,
            groupBy,
            categoryOrder,
            temporalOrder,
            indexBy,
            axisLabelOrientation,
            chartValueOrientation,
            chartValueType,
            layout,
            data,
            totalClaims,
        } = chart;
        const chartId = idMap[id] || String(id);
        const normalizedVariant = variant?.toLowerCase();
        const isTemporalChart = groupBy === "year" || groupBy === "quarter";
        const dataArray = Array.isArray(data) ? data : [];
        const categoryLabels = getOrderedLabels(
            categoryOrder,
            normalizedVariant === "donut"
                ? dataArray.flatMap((group) =>
                      Array.isArray(group.values)
                          ? group.values.map((value) => value.label)
                          : []
                  )
                : dataArray
                      .map((item) => item.label || item.group || item[indexBy])
                      .filter((label) => label != null)
        );
        const temporalLabels = isTemporalChart
            ? getOrderedLabels(
                  temporalOrder,
                  dataArray.map((item) => item.temporalLabel).filter(Boolean)
              )
            : [];

        const base = {
            id: chartId,
            title,
            description,
            accessibilityText,
            variant: normalizedVariant,
            indexBy,
            axisLabelOrientation,
            chartValueOrientation,
            chartValueType,
            layout,
            groupBy,
            categoryOrder,
            temporalOrder,
            temporalLabels,
            clientClaimCount: totalClaims,
        };

        if (data && data.message) {
            return {
                ...base,
                error: true,
                errorMessage: data.message,
                data: [],
            };
        }

        if (normalizedVariant === "donut") {
            const groupsByPeriod = new Map(
                dataArray.map((group) => [group.temporalLabel, group])
            );
            const transformed = isTemporalChart
                ? temporalLabels.map((period) => {
                      const group = groupsByPeriod.get(period) || {};
                      const valuesByLabel = new Map(
                          (Array.isArray(group.values) ? group.values : []).map(
                              (value) => [value.label, value.value]
                          )
                      );
                      return {
                          temporalLabel: period,
                          values: categoryLabels.map((label) => ({
                              gender: label,
                              value: valuesByLabel.get(label) ?? 0,
                          })),
                      };
                  })
                : getBenchmarkDonutGroups(dataArray).map((group) =>
                      (Array.isArray(categoryOrder)
                          ? categoryLabels.map((label) => ({
                                label,
                                value:
                                    group.values?.find(
                                        (value) => value.label === label
                                    )?.value ?? 0,
                            }))
                          : group.values || []
                      ).map((value) => ({
                          gender: value.label,
                          value: value.value ?? 0,
                      }))
                  );
            return { ...base, data: transformed };
        }

        if (isTemporalChart) {
            const formattedDataByLabel = new Map(
                categoryLabels.map((label) => [label, { [indexBy]: label }])
            );
            dataArray.forEach((item) => {
                const label = item.label || item.group || item[indexBy];
                if (label == null || !item.temporalLabel) return;

                if (!formattedDataByLabel.has(label)) return;
                const formattedItem = formattedDataByLabel.get(label);
                formattedItem[item.temporalLabel] = normalizeBarValue(
                    item.selectedOrganizations
                );
                formattedItem[`${item.temporalLabel}Display`] =
                    normalizeBarDisplayValue(item.selectedOrganizations);
            });
            const formattedData = [...formattedDataByLabel.values()];
            formattedData.forEach((item) => {
                temporalLabels.forEach((label) => {
                    if (item[label] == null) {
                        item[label] = 0;
                        item[`${label}Display`] = 0;
                    }
                });
            });
            return { ...base, data: formattedData };
        }

        const formattedData =
            data?.map((item) => ({
                [indexBy]: item.label || item.group || item[indexBy],
                Org: normalizeBarValue(item.selectedOrganizations),
                OrgDisplay: normalizeBarDisplayValue(
                    item.selectedOrganizations
                ),
                Industrypeers: normalizeBarValue(item.industryPeers),
                IndustrypeersDisplay: normalizeBarDisplayValue(
                    item.industryPeers
                ),
                unumclient: normalizeBarValue(item.unumBlock),
                unumclientDisplay: normalizeBarDisplayValue(item.unumBlock),
            })) || [];
        const formattedDataByLabel = new Map(
            formattedData.map((item) => [item[indexBy], item])
        );
        return {
            ...base,
            data: Array.isArray(categoryOrder)
                ? categoryLabels.map(
                      (label) =>
                          formattedDataByLabel.get(label) || {
                              [indexBy]: label,
                              Org: 0,
                              OrgDisplay: 0,
                              Industrypeers: 0,
                              IndustrypeersDisplay: 0,
                              unumclient: 0,
                              unumclientDisplay: 0,
                          }
                  )
                : formattedData,
        };
    });
};

export default formatDashboardData;
